// Calendar year utility functions
// Calendar year quarters: Q1 (Jan-Mar), Q2 (Apr-Jun), Q3 (Jul-Sep), Q4 (Oct-Dec)

/**
 * Gets the calendar year for a given date.
 */
export function getCalendarYear(date: Date): number {
  return date.getFullYear();
}

/**
 * Gets the calendar quarter (1-4) for a given date.
 * Q1: Jan-Mar, Q2: Apr-Jun, Q3: Jul-Sep, Q4: Oct-Dec
 */
export function getCalendarQuarter(date: Date): number {
  const month = date.getMonth() + 1; // 1-12
  return Math.floor((month - 1) / 3) + 1;
}

/**
 * Gets the start date of a calendar year.
 */
export function getCalendarYearStart(year: number): Date {
  return new Date(year, 0, 1); // January 1
}

/**
 * Gets the end date of a calendar year.
 */
export function getCalendarYearEnd(year: number): Date {
  return new Date(year, 11, 31); // December 31
}

/**
 * Gets the start and end dates for a specific calendar quarter.
 */
export function getCalendarQuarterRange(year: number, quarter: number): { start: Date; end: Date } {
  const startMonth = (quarter - 1) * 3; // 0-based month index
  const start = new Date(year, startMonth, 1);
  const end = new Date(year, startMonth + 3, 0); // Last day of the third month
  return { start, end };
}

/**
 * Checks if a date falls within the current calendar year.
 */
export function isInCurrentCalendarYear(date: Date): boolean {
  return getCalendarYear(date) === getCalendarYear(new Date());
}

/**
 * Checks if a date falls within the current calendar quarter.
 */
export function isInCurrentCalendarQuarter(date: Date): boolean {
  const now = new Date();
  return getCalendarYear(date) === getCalendarYear(now) && 
         getCalendarQuarter(date) === getCalendarQuarter(now);
}

/**
 * Gets the current calendar year.
 */
export function getCurrentCalendarYear(): number {
  return getCalendarYear(new Date());
}

/**
 * Gets the current calendar quarter.
 */
export function getCurrentCalendarQuarter(): number {
  return getCalendarQuarter(new Date());
}

/**
 * Gets the next calendar quarter relative to the current date.
 */
export function getNextCalendarQuarter(): { year: number; quarter: number } {
  const currentYear = getCurrentCalendarYear();
  const currentQuarter = getCurrentCalendarQuarter();
  
  if (currentQuarter === 4) {
    return { year: currentYear + 1, quarter: 1 };
  }
  return { year: currentYear, quarter: currentQuarter + 1 };
}

/**
 * Gets the previous calendar quarter relative to the current date.
 */
export function getPreviousCalendarQuarter(): { year: number; quarter: number } {
  const currentYear = getCurrentCalendarYear();
  const currentQuarter = getCurrentCalendarQuarter();
  
  if (currentQuarter === 1) {
    return { year: currentYear - 1, quarter: 4 };
  }
  return { year: currentYear, quarter: currentQuarter - 1 };
}

/**
 * Formats a calendar year and quarter as a string (e.g., "2024 Q3")
 */
export function formatCalendarYearQuarter(year: number, quarter: number): string {
  return `${year} Q${quarter}`;
}

/**
 * Gets a suggested name for a review cycle based on calendar year and quarter
 */
export function getSuggestedReviewCycleName(year: number, quarter: number): string {
  return `${formatCalendarYearQuarter(year, quarter)} Review`;
}
